import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTruncatedTooltip } from '@/core/hooks/useTruncatedTooltip';
import './SearchDropdown.scss';

export interface SearchResult {
  id: string;
  name: string;
  type: string;
  channel?: string;
  label?: string;
}

interface SearchDropdownProps {
  results: SearchResult[];
  onSelect: (result: SearchResult) => void;
  isVisible: boolean;
}

export const SearchDropdown: React.FC<SearchDropdownProps> = ({
  results,
  onSelect,
  isVisible,
}) => {
  const { t } = useTranslation();
  const {
    tooltip: itemTooltip,
    showTooltip: showItemTooltip,
    hideTooltip: hideItemTooltip,
  } = useTruncatedTooltip({ targetSelector: '.search-dropdown__label' });
  
  if (!isVisible || results.length === 0) return null;

  const typeLabels: Record<string, string> = {
    group: `${t('header.searchGroup')}:`,
    theme: `${t('header.searchTheme')}:`,
  };

  const getTypeLabel = (result: SearchResult) => {
    if (result.label) return result.label;
    if (result.type !== 'group' && result.type !== 'theme' && result.channel) {
      return t('header.searchProfileLabel', { channel: result.channel, name: result.name });
    }

    return typeLabels[result.type] || `${t('header.searchProfile')} ${result.type.charAt(0).toUpperCase() + result.type.slice(1)}:`;
  };

  return (
    <div className="search-dropdown" onScroll={hideItemTooltip}>
      <ul className="search-dropdown__list">
        {results.map((result) => (
          <li key={`${result.type}-${result.id}`} className="search-dropdown__item">
            <button
              type="button"
              className="search-dropdown__button"
              onClick={() => onSelect(result)}
              onMouseEnter={(event) => showItemTooltip(event, result.label ?? getTypeLabel(result))}
              onFocus={(event) => showItemTooltip(event, result.label ?? getTypeLabel(result))}
              onMouseLeave={hideItemTooltip}
              onBlur={hideItemTooltip}
            >
              <span className="search-dropdown__label">
                {result.label ?? getTypeLabel(result)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {itemTooltip && (
        <span
          className="search-dropdown__item-tooltip"
          role="tooltip"
          style={{
            top: `${itemTooltip.top}px`,
            left: `${itemTooltip.left}px`,
          }}
        >
          {itemTooltip.text}
        </span>
      )}
    </div>
  );
};
